import React, { useState } from "react";
import { useGetCashFlowQuery } from "../services/api";
import { getApiV1Url } from "../services/apiConfig";
import { FiTrendingUp, FiTrendingDown, FiDollarSign, FiDownload, FiBarChart2 } from "react-icons/fi";

function formatCurrency(value) {
  return new Intl.NumberFormat("en-ET", { style: "currency", currency: "ETB" }).format(value || 0);
}

export default function CashFlowPage() {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const { data, isLoading } = useGetCashFlowQuery({ start_date: startDate, end_date: endDate });
  const cashFlow = data?.data || {};

  const income = cashFlow.income || {};
  const expenses = cashFlow.expenses || {};
  const expensesByCategory = expenses.by_category || [];

  const handleExportExcel = () => {
    const params = new URLSearchParams();
    if (startDate) params.append("start_date", startDate);
    if (endDate) params.append("end_date", endDate);
    
    const apiUrl = getApiV1Url();
    window.open(`${apiUrl}/cashflow/export?${params.toString()}`, "_blank");
  };

  if (isLoading) {
    return (
      <div style={{ background: "#F8F9FA", minHeight: "100vh", padding: "2rem" }}>
        <div style={{ marginBottom: "2rem" }}>
          <div style={{ 
            height: "32px", 
            width: "180px", 
            background: "#E2E8F0", 
            borderRadius: "6px",
            marginBottom: "0.75rem",
            animation: "pulse 1.5s ease-in-out infinite"
          }}></div>
        </div>
        <div style={{ 
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
          gap: "1.25rem"
        }}>
          {[1,2,3].map(i => (
            <div key={i} style={{
              background: "white",
              border: "1px solid #E2E8F0",
              borderRadius: "12px",
              padding: "1.5rem",
              animation: "pulse 1.5s ease-in-out infinite"
            }}>
              <div style={{ height: "16px", width: "60%", background: "#E2E8F0", borderRadius: "4px", marginBottom: "1rem" }}></div>
              <div style={{ height: "32px", width: "40%", background: "#E2E8F0", borderRadius: "4px" }}></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: "#F8F9FA", minHeight: "100vh", padding: "2rem" }}>
      {/* Page Header */}
      <div style={{ 
        display: "flex", 
        justifyContent: "space-between", 
        alignItems: "center", 
        marginBottom: "2rem" 
      }}>
        <div>
          <h1 style={{ 
            fontSize: "2rem", 
            marginBottom: "0.5rem", 
            fontWeight: 600,
            color: "#1E293B"
          }}>
            Cash Flow Management
          </h1>
          <p style={{ color: "#64748B", fontSize: "0.95rem" }}>
            Real-time income and expense tracking
          </p>
        </div>
        <button 
          onClick={handleExportExcel}
          style={{
            padding: "0.75rem 1.5rem",
            background: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
            color: "white",
            border: "none",
            borderRadius: "8px",
            fontSize: "0.95rem",
            fontWeight: 600,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            boxShadow: "0 2px 8px rgba(16, 185, 129, 0.3)",
            transition: "all 0.2s"
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-1px)";
            e.currentTarget.style.boxShadow = "0 4px 12px rgba(16, 185, 129, 0.4)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "0 2px 8px rgba(16, 185, 129, 0.3)";
          }}
        >
          <FiDownload /> Export to Excel
        </button>
      </div>

      {/* Date Filter Card */}
      <div style={{
        background: "white",
        border: "1px solid #E2E8F0",
        borderRadius: "12px",
        padding: "1.5rem",
        marginBottom: "2rem"
      }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: "1rem", alignItems: "end" }}>
          <div>
            <label style={{ 
              display: "block", 
              fontSize: "0.875rem", 
              fontWeight: 600, 
              color: "#1E293B", 
              marginBottom: "0.5rem" 
            }}>
              Start Date
            </label>
            <input 
              type="date" 
              value={startDate} 
              onChange={(e) => setStartDate(e.target.value)}
              style={{
                width: "100%",
                padding: "0.625rem 0.875rem",
                border: "1px solid #E2E8F0",
                borderRadius: "8px",
                fontSize: "0.95rem",
                color: "#1E293B",
                transition: "all 0.2s"
              }}
              onFocus={(e) => e.currentTarget.style.borderColor = "#3B82F6"}
              onBlur={(e) => e.currentTarget.style.borderColor = "#E2E8F0"}
            />
          </div>
          <div>
            <label style={{ 
              display: "block", 
              fontSize: "0.875rem", 
              fontWeight: 600, 
              color: "#1E293B", 
              marginBottom: "0.5rem" 
            }}>
              End Date
            </label>
            <input 
              type="date" 
              value={endDate} 
              onChange={(e) => setEndDate(e.target.value)}
              style={{
                width: "100%",
                padding: "0.625rem 0.875rem",
                border: "1px solid #E2E8F0",
                borderRadius: "8px",
                fontSize: "0.95rem",
                color: "#1E293B",
                transition: "all 0.2s"
              }}
              onFocus={(e) => e.currentTarget.style.borderColor = "#3B82F6"}
              onBlur={(e) => e.currentTarget.style.borderColor = "#E2E8F0"}
            />
          </div>
          <button 
            onClick={() => { setStartDate(""); setEndDate(""); }}
            style={{
              padding: "0.625rem 1.5rem",
              background: "transparent",
              border: "1px solid #E2E8F0",
              borderRadius: "8px",
              fontSize: "0.95rem",
              fontWeight: 600,
              color: "#64748B",
              cursor: "pointer",
              transition: "all 0.2s"
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#F8FAFC";
              e.currentTarget.style.borderColor = "#CBD5E1";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
              e.currentTarget.style.borderColor = "#E2E8F0";
            }}
          >
            Clear
          </button>
        </div>
      </div>

      {/* Main KPI Cards */}
      <div style={{ 
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
        gap: "1.25rem",
        marginBottom: "2rem"
      }}>
        {/* Total Income */}
        <div style={{ 
          background: "white",
          border: "1px solid #E2E8F0",
          borderRadius: "12px",
          padding: "1.5rem",
          transition: "all 0.3s",
          cursor: "pointer"
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)";
          e.currentTarget.style.transform = "translateY(-2px)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = "none";
          e.currentTarget.style.transform = "translateY(0)";
        }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
            <div style={{ 
              width: "48px",
              height: "48px",
              borderRadius: "10px",
              background: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}>
              <FiTrendingUp size={24} color="white" />
            </div>
          </div>
          <div style={{ fontSize: "0.85rem", color: "#64748B", marginBottom: "0.5rem", fontWeight: 500 }}>
            Total Income
          </div>
          <div style={{ fontSize: "1.75rem", fontWeight: 700, color: "#10B981", marginBottom: "0.5rem" }}>
            {formatCurrency(income.total)}
          </div>
          <div style={{ fontSize: "0.8rem", color: "#64748B" }}>
            {income.sales_count || 0} sales
          </div>
        </div>

        {/* Total Expenses */}
        <div style={{ 
          background: "white",
          border: "1px solid #E2E8F0",
          borderRadius: "12px",
          padding: "1.5rem",
          transition: "all 0.3s",
          cursor: "pointer"
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)";
          e.currentTarget.style.transform = "translateY(-2px)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = "none";
          e.currentTarget.style.transform = "translateY(0)";
        }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
            <div style={{ 
              width: "48px",
              height: "48px",
              borderRadius: "10px",
              background: "linear-gradient(135deg, #EF4444 0%, #DC2626 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}>
              <FiTrendingDown size={24} color="white" />
            </div>
          </div>
          <div style={{ fontSize: "0.85rem", color: "#64748B", marginBottom: "0.5rem", fontWeight: 500 }}>
            Total Expenses
          </div>
          <div style={{ fontSize: "1.75rem", fontWeight: 700, color: "#EF4444", marginBottom: "0.5rem" }}>
            {formatCurrency(expenses.total)}
          </div>
          <div style={{ fontSize: "0.8rem", color: "#64748B" }}>
            {expensesByCategory.length} categories
          </div>
        </div>

        {/* Net Profit */}
        <div style={{ 
          background: "white",
          border: "1px solid #E2E8F0",
          borderRadius: "12px",
          padding: "1.5rem",
          transition: "all 0.3s",
          cursor: "pointer"
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)";
          e.currentTarget.style.transform = "translateY(-2px)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = "none";
          e.currentTarget.style.transform = "translateY(0)";
        }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
            <div style={{ 
              width: "48px",
              height: "48px",
              borderRadius: "10px",
              background: cashFlow.net_profit >= 0 
                ? "linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)"
                : "linear-gradient(135deg, #EF4444 0%, #DC2626 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}>
              <FiDollarSign size={24} color="white" />
            </div>
          </div>
          <div style={{ fontSize: "0.85rem", color: "#64748B", marginBottom: "0.5rem", fontWeight: 500 }}>
            Net Profit
          </div>
          <div style={{ 
            fontSize: "1.75rem", 
            fontWeight: 700, 
            color: cashFlow.net_profit >= 0 ? "#8B5CF6" : "#EF4444",
            marginBottom: "0.5rem"
          }}>
            {formatCurrency(cashFlow.net_profit)}
          </div>
          <div style={{ fontSize: "0.8rem", color: "#64748B" }}>
            {cashFlow.net_profit >= 0 ? "✓ Profitable" : "⚠ Loss"}
          </div>
        </div>
      </div>

      {/* Detailed Breakdown Cards */}
      <div style={{ 
        display: "grid", 
        gridTemplateColumns: "repeat(auto-fit, minmax(350px, 1fr))", 
        gap: "1.25rem"
      }}>
        {/* Income Breakdown */}
        <div style={{
          background: "white",
          border: "1px solid #E2E8F0",
          borderRadius: "12px",
          padding: "1.5rem"
        }}>
          <div style={{ 
            display: "flex", 
            alignItems: "center", 
            gap: "0.75rem", 
            marginBottom: "1.25rem",
            paddingBottom: "1rem",
            borderBottom: "1px solid #F1F5F9"
          }}>
            <div style={{
              width: "40px",
              height: "40px",
              borderRadius: "8px",
              background: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}>
              <FiBarChart2 color="white" size={20} />
            </div>
            <h3 style={{ fontSize: "1.125rem", fontWeight: 600, color: "#1E293B" }}>
              Income Breakdown
            </h3>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
            <div style={{ 
              display: "flex", 
              justifyContent: "space-between", 
              padding: "1rem", 
              background: "#D1FAE5", 
              borderRadius: "8px"
            }}>
              <span style={{ color: "#047857", fontWeight: 600, fontSize: "0.9rem" }}>Cash Sales</span>
              <span style={{ fontWeight: 700, color: "#047857", fontSize: "0.95rem" }}>{formatCurrency(income.cash)}</span>
            </div>
            <div style={{ 
              display: "flex", 
              justifyContent: "space-between", 
              padding: "1rem", 
              background: "#F8FAFC", 
              borderRadius: "8px"
            }}>
              <span style={{ color: "#64748B", fontWeight: 600, fontSize: "0.9rem" }}>Bank Transfers</span>
              <span style={{ fontWeight: 700, color: "#3B82F6", fontSize: "0.95rem" }}>{formatCurrency(income.bank)}</span>
            </div>
            <div style={{ 
              display: "flex", 
              justifyContent: "space-between", 
              padding: "1rem", 
              background: "#F5F3FF", 
              borderRadius: "8px",
              border: "2px solid #8B5CF6"
            }}>
              <span style={{ color: "#5B21B6", fontWeight: 700, fontSize: "0.9rem" }}>Total Income</span>
              <span style={{ fontWeight: 800, color: "#8B5CF6", fontSize: "1.125rem" }}>{formatCurrency(income.total)}</span>
            </div>
          </div>
        </div>

        {/* Expenses by Category */}
        <div style={{
          background: "white",
          border: "1px solid #E2E8F0",
          borderRadius: "12px",
          padding: "1.5rem"
        }}>
          <div style={{ 
            display: "flex", 
            alignItems: "center", 
            gap: "0.75rem", 
            marginBottom: "1.25rem",
            paddingBottom: "1rem",
            borderBottom: "1px solid #F1F5F9"
          }}>
            <div style={{
              width: "40px",
              height: "40px",
              borderRadius: "8px",
              background: "linear-gradient(135deg, #EF4444 0%, #DC2626 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}>
              <FiDollarSign color="white" size={20} />
            </div>
            <h3 style={{ fontSize: "1.125rem", fontWeight: 600, color: "#1E293B" }}>
              Expenses by Category
            </h3>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
            {expensesByCategory.length === 0 ? (
              <div style={{ 
                padding: "2rem", 
                textAlign: "center", 
                color: "#64748B",
                background: "#F8FAFC",
                borderRadius: "8px"
              }}>
                No expenses recorded
              </div>
            ) : (
              expensesByCategory.map((cat) => (
                <div 
                  key={cat.category} 
                  style={{ 
                    display: "flex", 
                    justifyContent: "space-between", 
                    alignItems: "center",
                    padding: "1rem", 
                    background: "#FEF2F2", 
                    borderRadius: "8px"
                  }}
                >
                  <span style={{ 
                    textTransform: "capitalize", 
                    color: "#991B1B", 
                    fontWeight: 600, 
                    fontSize: "0.9rem" 
                  }}>
                    {cat.category}
                  </span>
                  <span style={{ fontWeight: 700, color: "#EF4444", fontSize: "0.95rem" }}>
                    {formatCurrency(cat.total)}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
